/**
 *  @class
 *  @function RecentlyViewedProducts
 */
if (!customElements.get('recently-viewed-products')) {
  class RecentlyViewedProducts extends HTMLElement {
    constructor() {
      super();

      this.container = this.querySelector('.products');
      this.currentId = this.dataset.productId;
      this.currentHandle = this.dataset.productHandle;
      this.max = 4;
    }
    async connectedCallback() {
      if (this.loading || !this.container) return;
      this.loading = true;
      try {
        await this.buildUrl();
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    }
    async resolveId(value) {
      if (/^\d+$/.test(value)) return value;
      if (value === this.currentHandle) return this.currentId;

      // Migrate the existing handle history without discarding it. Try the
      // visitor's locale first, then the shop's default-language product URL.
      const root = window.Shopify?.routes?.root || '/';
      for (const prefix of new Set([root, '/'])) {
        try {
          const response = await fetch(`${prefix}products/${encodeURIComponent(value)}.js`);
          if (!response.ok) continue;
          const product = await response.json();
          if (product.id) return String(product.id);
        } catch (error) {
          // An old/translated handle may be unavailable in the current market.
        }
      }
      return null;
    }
    async buildUrl() {
      const history = getRecentlyViewedProducts();
      const ids = [];
      const resolved = new Map();
      // Resolve old entries in small batches; IDs need no extra requests.
      for (let offset = 0; offset < history.length && ids.length < this.max; offset += this.max) {
        const batch = history.slice(offset, offset + this.max);
        const values = await Promise.all(batch.map(value => this.resolveId(value)));
        values.forEach((id, index) => {
          if (id) resolved.set(batch[index], id);
          if (id && id !== this.currentId && !ids.includes(id) && ids.length < this.max) {
            ids.push(id);
          }
        });
      }
      if (resolved.size) {
        // Re-read so a product recorded during the requests is not overwritten.
        saveRecentlyViewedProducts(getRecentlyViewedProducts().map(value => resolved.get(value) || value));
      }
      if (!ids.length) return;

      const query = ids.map(id => `id:${id}`).join(' OR ');
      const url = `${theme.routes.search_url}?view=recently-viewed&type=product&q=${encodeURIComponent(query)}`;
      await this.fetchProducts(url, ids);
    }
    async fetchProducts(url, ids) {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Recently viewed request failed: ${response.status}`);
      const html = document.createElement('div');
      html.innerHTML = await response.text();
      const cards = new Map([...html.querySelectorAll('.products > [data-product-id]')]
        .map(card => [card.dataset.productId, card]));
      const ordered = ids.map(id => cards.get(id)).filter(Boolean);
      this.container.replaceChildren(...ordered);
      this.classList.toggle('product-recommendations--loaded', ordered.length > 0);
    }
  }

  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
